import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Helmet } from 'react-helmet-async';
import {
  Grid,
  Button,
  Box,
  Modal,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';

import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { deleteImage, getUserImages, saveImage, updateImage } from '../../../../apiCalls';
import { DeleteConfirmationDialog } from 'src/components/Dialogs/DeleteConfirmationDialog';
import { ImageCard } from './ImageCard';
import { Image } from "../../../../interfaces";
import logger from 'src/logger';
// Define the interface for image properties


// Define the available price options


const ViewImages: React.FC = () => {
  const [images, setImages] = useState<Image[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [editedCards, setEditedCards] = useState<boolean[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const { user } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = (imagePath: string) => {
    setSelectedImage(imagePath);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleDeleteClick = (imageId: string) => {
    setImageToDelete(imageId);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (imageToDelete) {
      await deleteUserImage(imageToDelete);
      setImageToDelete(null);
    }
    setConfirmDeleteOpen(false);
  };

  const cancelDelete = () => {
    setImageToDelete(null);
    setConfirmDeleteOpen(false);
  };

  const deleteUserImage = async (imageId: string) => {
    try {
      await deleteImage(user.accessToken, user.uid, imageId);
      setImages((prevImages) =>
        prevImages.filter((image) => image.id !== imageId)
      );
    } catch (error) {
      logger.error('Failed to delete image:', error);
    }
  };

  const updateImageMetadata = async (imageId: string) => {
    const image = images.find((img) => img.id === imageId);
    if (!image) return;
    logger.info('Updating image metadata for ID:', imageId);

    const body = {
      tags: image.tags,
      species: image.species,
      weight: image.weight,
    };

    try {
      await updateImage(user.accessToken, user.uid, imageId, body);
      logger.info('Image metadata updated:', body);
      setEditedCards((prev) => prev.map((edited, idx) => idx === images.findIndex(img => img.id === imageId) ? false : edited));
    } catch (error) {
      logger.error('Failed to update image metadata:', error);
    }
  };

  const handleImageData = (data: Image[] | null) => {
    logger.info('Fetched image data:', data);
    const safeData = data || [];
    setImages(safeData);
    setExpanded(Array(safeData.length).fill(false));
    setEditedCards(Array(safeData.length).fill(false));
    setLoading(false);
  };

  const handleMetadataChange = (index: number, field: 'price' | 'tags' | 'species' | 'weight', value: string | number) => {
    setImages((prevImages) =>
      prevImages.map((image, idx) =>
        idx === index ? { ...image, [field]: value } : image
      )
    );
    setEditedCards((prev) => prev.map((edited, idx) => idx === index ? true : edited));
  };

  const fetchUserImages = async () => {
    try {
      const data = await getUserImages(user.accessToken, user.uid);
      handleImageData(data);
    } catch (error) {
      logger.error('Error fetching user images:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserImages();
  }, [user]);

  return (
    <div style={{ padding: isMobile ? '0 15px' : '0 15px' }}>
      <Helmet>
        <title>Your Catch Reports</title>
      </Helmet>
      <PageTitleWrapper>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            marginBottom: 1,
            textAlign: isMobile ? 'center' : 'left',
            padding: '4px 4px',
          }}
        >
          <PageTitle heading="Your Catch Reports" />
          <Button
            variant="contained"
            color="primary"
            size="small"
            component={RouterLink}
            to="/images/addsingle"
            startIcon={<CloudUploadIcon />}
            sx={{
              mt: isMobile ? 1 : 0,
              whiteSpace: 'nowrap',
              padding: '6px 16px',
              fontSize: '0.875rem',
            }}
          >
            Add Catch Report
          </Button>
        </Box>
      </PageTitleWrapper>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="image-list">
          <Grid container spacing={isMobile ? 2 : 7}>
            <DeleteConfirmationDialog
              open={confirmDeleteOpen}
              onClose={cancelDelete}
              onConfirm={confirmDelete}
            />
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <ImageCard
                  image={image}
                  index={index}
                  isMobile={isMobile}
                  edited={editedCards[index]}
                  onMetadataChange={handleMetadataChange}
                  onDeleteClick={handleDeleteClick}
                  onUpdateMetadata={updateImageMetadata}
                  onImageClick={handleOpen}
                  expanded={expanded[index]}
                  toggleExpand={() => setExpanded((prev) => prev.map((exp, i) => (i === index ? !exp : exp)))}
                />
              </Grid>
            ))}
          </Grid>
          <Modal
            open={modalOpen}
            onClose={handleClose}
            aria-labelledby="image-modal-title"
            aria-describedby="image-modal-description"
          >
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90vw',
                height: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
              }}
            >
              <Box sx={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }}>
                <img
                  src={selectedImage}
                  alt="Full Size"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                    borderRadius: '12px'
                  }}
                />
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    backgroundColor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': {
                      backgroundColor: 'background.paper',
                      opacity: 0.9
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default ViewImages;
